import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Button } from "flowbite-react";
import { AiOutlineLoading } from "react-icons/ai";
import { NavBar, SideNav } from "../../components";
import Breadcrumb from "../../components/Breadcrumb";
import { fetchCrawlRuns, setCrawlSchoolFilter } from "../../redux/slices/crawler";
import { fetchReviewCounts } from "../../redux/slices/review";

function formatTs(value) {
  if (!value) return "—";
  const date =
    typeof value?.toDate === "function"
      ? value.toDate()
      : value?._seconds
        ? new Date(value._seconds * 1000)
        : typeof value === "string" || typeof value === "number"
          ? new Date(value)
          : null;
  if (!date || Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString();
}

function pagesCrawled(run) {
  const s = run.stats || {};
  if (s.pagesCrawled != null) return s.pagesCrawled;
  if (s.pages != null) return s.pages;
  const verified = s.verified || 0;
  const changeSets = s.changeSets || 0;
  return verified + changeSets || "—";
}

function fieldsFound(run) {
  const s = run.stats || {};
  if (s.fieldsFound != null) return s.fieldsFound;
  if (s.changeSets != null) return s.changeSets;
  return "—";
}

function coverageCell(run) {
  const s = run.stats || {};
  const raw = s.coverage ?? s.avgCoverage ?? run.coverage;
  if (typeof raw === "number") return `${Math.round(raw * 100)}%`;
  return "—";
}

function llmCost(run) {
  const s = run.stats || {};
  const raw = s.llmCost ?? s.estCostUSD ?? run.llmCost ?? run.estCostUSD;
  if (typeof raw === "number") return `$${raw.toFixed(4)}`;
  return null;
}

function statusClass(status) {
  if (status === "completed") return "bg-green-50 text-green-800";
  if (status === "running") return "bg-blue-50 text-blue-800";
  if (status === "failed" || status === "error") {
    return "bg-red-50 text-red-800";
  }
  return "bg-gray-100 text-gray-700";
}

const CrawlerHealth = () => {
  const dispatch = useDispatch();
  const { crawlRuns, schoolIdFilter, crawlLoading, error } = useSelector(
    (state) => state.crawler
  );
  const { counts } = useSelector((state) => state.review);
  const [schoolInput, setSchoolInput] = useState(schoolIdFilter || "");

  useEffect(() => {
    dispatch(fetchReviewCounts({ status: "pending" }));
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchCrawlRuns({ schoolId: schoolIdFilter, limit: 50 }));
  }, [dispatch, schoolIdFilter]);

  const applyFilter = (e) => {
    e.preventDefault();
    dispatch(setCrawlSchoolFilter(schoolInput.trim()));
  };

  const needsAttention = counts?.needs_attention ?? 0;

  return (
    <div className="antialiased bg-gray-50 dark:bg-gray-900 min-h-screen">
      <NavBar />
      <SideNav />
      <main className="p-4 md:ml-64 h-auto pt-20">
        <div className="sticky top-14 z-10 bg-gray-50 dark:bg-gray-900">
          <Breadcrumb />
        </div>

        <section className="mb-6 flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-200">
              Crawler health
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              Recent crawl runs per school. Filter by schoolId to focus a
              single institution.
            </p>
          </div>
          <Link
            to="/review?tier=needs_attention"
            className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm hover:border-amber-400"
          >
            <span className="text-2xl font-semibold text-amber-900 block">
              {needsAttention}
            </span>
            <span className="font-medium text-amber-900">
              Needs attention
            </span>
            <span className="block text-xs text-amber-700 mt-0.5">
              Open review queue →
            </span>
          </Link>
        </section>

        <form
          onSubmit={applyFilter}
          className="mb-4 flex flex-wrap gap-2 items-end"
        >
          <label className="text-sm text-gray-600">
            School ID
            <input
              type="text"
              className="mt-1 block w-64 rounded-lg border-gray-300 text-sm"
              value={schoolInput}
              placeholder="Filter by schoolId"
              onChange={(e) => setSchoolInput(e.target.value)}
            />
          </label>
          <Button color="purple" type="submit" disabled={crawlLoading}>
            Apply
          </Button>
          {schoolIdFilter && (
            <Button
              color="gray"
              type="button"
              onClick={() => {
                setSchoolInput("");
                dispatch(setCrawlSchoolFilter(""));
              }}
            >
              Clear
            </Button>
          )}
          {crawlLoading && (
            <AiOutlineLoading className="animate-spin text-purple-700 mb-2" />
          )}
        </form>

        {error && (
          <p className="text-sm text-red-600 mb-3">{String(error)}</p>
        )}

        <div className="overflow-x-auto bg-white rounded-lg border border-gray-200">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left text-gray-600">
              <tr>
                <th className="px-4 py-3">School</th>
                <th className="px-4 py-3">Started</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Pages</th>
                <th className="px-4 py-3">Fields</th>
                <th className="px-4 py-3">Coverage</th>
                <th className="px-4 py-3">Errors</th>
                <th className="px-4 py-3">LLM cost</th>
              </tr>
            </thead>
            <tbody>
              {!crawlRuns.length && !crawlLoading && (
                <tr>
                  <td
                    colSpan={8}
                    className="px-4 py-8 text-center text-gray-500"
                  >
                    No crawl runs
                    {schoolIdFilter ? ` for ${schoolIdFilter}` : ""}.
                  </td>
                </tr>
              )}
              {crawlRuns.map((run) => {
                const cost = llmCost(run);
                return (
                  <tr
                    key={run.id}
                    className="border-t border-gray-100 hover:bg-purple-50/40"
                  >
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">
                        {run.schoolName || run.schoolId || "—"}
                      </div>
                      {run.schoolId && (
                        <code className="text-xs text-gray-400">
                          {run.schoolId}
                        </code>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                      {formatTs(run.startedAt)}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`rounded-full px-2 py-0.5 text-xs ${statusClass(
                          run.status
                        )}`}
                      >
                        {run.status || "—"}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {pagesCrawled(run)}
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {fieldsFound(run)}
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {coverageCell(run)}
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {run.stats?.errors ?? "—"}
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {cost ?? "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
};

export default CrawlerHealth;
